import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import SanaaMark from "@/components/SanaaMark";
import LangSwitcher from "@/components/LangSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowRight, Loader2 } from "lucide-react";

const ARABIC_PATTERN = "https://images.pexels.com/photos/12494576/pexels-photo-12494576.jpeg";

export default function Auth() {
  const { user, login, signup } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("creator");
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/app");
  }, [user, nav]);

  useEffect(() => {
    api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await signup({ email, password, name, role });
        toast.success("Account created");
      }
      nav("/app");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = async (d) => {
    setEmail(d.email);
    setPassword(d.password);
    setMode("login");
    setLoading(true);
    try {
      await login(d.email, d.password);
      toast.success(`Signed in as ${d.name}`);
      nav("/app");
    } catch {
      toast.error("Demo sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background text-foreground">
      {/* Visual side */}
      <div className="relative hidden lg:block overflow-hidden border-e border-border/60">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25 dark:opacity-30"
          style={{ backgroundImage: `url(${ARABIC_PATTERN})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/40 via-background/70 to-background" />
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="relative h-full flex flex-col p-12 justify-between">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <SanaaMark size={28} />
            <span className="font-display text-lg">Sanaa AI</span>
          </Link>
          <div>
            <h2 className="font-serif text-5xl xl:text-6xl tracking-tight leading-[1] max-w-md">
              One sign-in.
              <br />
              <span className="italic text-muted-foreground">Five AI products.</span>
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(135deg, #E1306C, #F56040, #833AB4)" }}
              >
                Zero compromise.
              </span>
            </h2>
            <p className="mt-6 max-w-md text-muted-foreground leading-relaxed">
              Sanaa Creative · Falcon AI · Mizan AI · Sahel AI · Sufi AI — all included in one identity.
            </p>
          </div>
          <div className="text-xs text-muted-foreground tracking-wider uppercase">
            Dubai · Lagos · Nairobi · Riyadh
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="relative flex flex-col">
        <div className="flex items-center justify-end gap-2 p-5">
          <LangSwitcher />
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center px-5 py-10">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-2 mb-8">
              <SanaaMark size={28} />
              <span className="font-display text-lg">Sanaa AI</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">
              {mode === "login" ? t("auth.welcome") : t("auth.create")}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {mode === "login" ? t("auth.welcome_sub") : t("auth.create_sub")}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              {mode === "signup" && (
                <>
                  <Field label={t("auth.name")}>
                    <input
                      data-testid="auth-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="input"
                      placeholder="Khadija Designer"
                    />
                  </Field>
                  <Field label={t("auth.role")}>
                    <select
                      data-testid="auth-role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="input"
                    >
                      <option value="creator">Creator / Marketer</option>
                      <option value="analyst">Financial Analyst</option>
                      <option value="engineer">Engineer / Researcher</option>
                      <option value="architect">Architect / Builder</option>
                      <option value="investor">Investor</option>
                    </select>
                  </Field>
                </>
              )}
              <Field label={t("auth.email")}>
                <input
                  data-testid="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input"
                  placeholder="you@company.com"
                />
              </Field>
              <Field label={t("auth.password")}>
                <input
                  data-testid="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="input"
                  placeholder="••••••••"
                />
              </Field>

              <button
                type="submit"
                data-testid="auth-submit"
                disabled={loading}
                className="group inline-flex w-full h-12 items-center justify-center gap-2 rounded-sm bg-foreground px-6 text-sm font-medium text-background hover:opacity-90 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    {mode === "login" ? t("auth.submit_login") : t("auth.submit_signup")}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform rtl:rotate-180" />
                  </>
                )}
              </button>
            </form>

            <button
              onClick={() => setMode((m) => (m === "login" ? "signup" : "login"))}
              data-testid="auth-switch-mode"
              className="mt-5 text-sm text-muted-foreground hover:text-foreground"
            >
              {mode === "login" ? t("auth.switch_to_signup") : t("auth.switch_to_login")}
            </button>

            {/* Demo accounts */}
            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("auth.demo")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {demos.map((d) => (
                  <button
                    key={d.email}
                    onClick={() => handleDemo(d)}
                    data-testid={`demo-account-${d.role}`}
                    disabled={loading}
                    className="text-start rounded-sm border border-border/60 bg-card/40 hover:bg-card transition-colors p-3"
                  >
                    <div className="text-sm font-medium">{d.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{d.role}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          height: 44px;
          padding: 0 14px;
          background: hsl(var(--input));
          border: 1px solid hsl(var(--border));
          border-radius: 2px;
          color: hsl(var(--foreground));
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input:focus {
          border-color: hsl(var(--ring));
          box-shadow: 0 0 0 3px hsl(var(--ring) / 0.15);
        }
        .input::placeholder { color: hsl(var(--muted-foreground)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
